import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import useResizeObserver from 'hooks/useResizeObserver';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import AppBar from 'housecall-ui/dist/DesignSystem/Navigation/TopNavigation/AppBar/AppBar';
import Toolbar from 'housecall-ui/dist/DesignSystem/Navigation/TopNavigation/Toolbar';
import HousecallThemeProvider from 'housecall-ui/dist/DesignSystem/Styles/HousecallThemeProvider';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import useMediaQuery from 'housecall-ui/dist/DesignSystem/Styles/useMediaQuery';
import useTheme from 'housecall-ui/dist/DesignSystem/Styles/useTheme';
import PropTypes from 'prop-types';
import { QueryClientProvider } from 'resources/ReactQuery/QueryClientProvider';
import { isPresent } from 'utils/isPresent';

import RedirectTo from 'components/Common/RedirectTo';
import useNavbarHeight from 'components/Common/Navigation/useNavbarHeight';
import HamburgerMenu from 'components/Toolbar/v2/HamburgerMenu';
import TabState from 'components/Toolbar/v2/NavigationTabState';

import ToolbarOrgLogo from '../ToolbarOrgLogo';

import GlobalSearchContainer from './GlobalSearch/GlobalSearchContainer';
import AddNew from './AddNew';
import AppsPopoverContainer from './AppsPopoverContainer';
import AvatarMenu from './AvatarMenu';
import Banner from './Banner';
import NavigationTabs from './NavigationTabs';
import OrgLogoContainer from './OrgLogoContainer';
import { getPlanPackageLogo, HVAC_PACKAGE_NAME } from './planPackageLogoUrls';
import ProImpersonationLogoutButton from './ProImpersonationLogoutButton';
import ShortcutIcons from './ShortcutIcons';

const MAX_WIDTH_TAB = 120;
export const APP_BAR_HEIGHT = 72;

const useStyles = makeStyles(() => ({
  toolbar: {
    height: APP_BAR_HEIGHT,
  },
  flexContainer: {
    display: 'flex',
  },
  navBarLogo: {
    paddingRight: 20,
  },
  flexNone: {
    flex: 'none',
  },
}));

function AppToolbar({ toggleTasksDrawer, toggleActivityFeedDrawer, toolbarProps, legacyToolbar }) {
  const theme = useTheme();
  const { toolbar, flexContainer, flexNone, navBarLogo } = useStyles();

  const [tabsContainerRef, tabsContainerSize] = useResizeObserver();
  const logoRef = useRef(null);

  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));

  const [anchorEl, setAnchorEl] = useState(null);

  const { setNavbarHeight, navbarHeight } = useNavbarHeight();

  const TabsComponent = smUp ? NavigationTabs : HamburgerMenu;

  const launchPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const redirectToApps = () => RedirectTo.appsBase();
  const onMyAppsClick = smUp ? launchPopover : redirectToApps;

  const {
    activeTab,
    canUsePipeline,
    canUsePriceBook,
    getStartedDisplaySettings,
    showReporting,
    showCompanySetup,
    quickbooksBadgeData,
    noticeBar,
    hideNoticeBar,
    isProImpersonated,
    industryPackageName,
    thumbLogoUrl,
    lightMode,
    pricebookBadgeData,
  } = toolbarProps;

  const showBanner = !hideNoticeBar && isPresent(noticeBar?.display_message);

  const allTabs = TabState({
    canUsePipeline,
    canUsePriceBook,
    getStartedDisplaySettings,
    showReporting,
    showCompanySetup,
    pricebookBadgeData,
  });
  const tabsToDisplay = allTabs.filter((tabItem) => tabItem.showTab);
  let dropdownList = [];
  if (smUp) {
    const tabsContainerWidth = tabsContainerSize?.width;
    const logoWidth = logoRef?.current?.offsetWidth || 0;
    const tabsQuantity = Math.ceil((tabsContainerWidth - logoWidth) / MAX_WIDTH_TAB);
    const tabsToRemoved = -(tabsToDisplay.length - tabsQuantity + 1);
    dropdownList = tabsToDisplay.slice(
      tabsQuantity > 0 && tabsQuantity < tabsToDisplay.length ? tabsToRemoved : tabsToDisplay.length
    );
  }

  const packageLogoUrl = getPlanPackageLogo({ industryPackageName, isMax: lightMode });
  const packageLogo = packageLogoUrl ? <ToolbarOrgLogo orgLogoUrl={packageLogoUrl} /> : null;

  const orgLogo = (
    <OrgLogoContainer
      usingHvacPackage={industryPackageName === HVAC_PACKAGE_NAME}
      orgLogoUrl={thumbLogoUrl}
      isMax={lightMode}
    />
  );

  const smProps = smUp ? { direction: 'row-reverse', justify: 'flex-end' } : {};

  return (
    <>
      <AppBar
        id="hc-toolbar"
        color="primary"
        position="fixed"
        ref={(el) => el && setNavbarHeight(el.clientHeight)}
        version="v2"
      >
        {showBanner && <Banner noticeBar={noticeBar} />}
        <Toolbar className={toolbar} version="v2">
          <Grid container alignItems="center" spacing={1} wrap="nowrap">
            <Grid
              container
              item
              wrap="nowrap"
              xs="auto"
              className={classNames(flexContainer)}
              alignItems="center"
              ref={tabsContainerRef}
              {...smProps}
            >
              <Grid item xs="auto" zeroMinWidth>
                <TabsComponent
                  activeTab={activeTab}
                  toolbarTabs={tabsToDisplay.slice(0, tabsToDisplay.length - dropdownList.length)}
                  dropdownList={dropdownList}
                  packageLogo={packageLogo}
                />
              </Grid>
              {orgLogo && (
                <Grid item xs="auto" ref={logoRef}>
                  <div className={navBarLogo}>{orgLogo}</div>
                </Grid>
              )}
            </Grid>
            {!legacyToolbar && (
              <Grid item xs>
                <HousecallThemeProvider mode="light">
                  <QueryClientProvider>
                    <GlobalSearchContainer />
                  </QueryClientProvider>
                </HousecallThemeProvider>
              </Grid>
            )}
            <Grid item xs="auto">
              <QueryClientProvider>
                <AddNew jobsFTU={getStartedDisplaySettings?.jobs_ftu} {...toolbarProps} />
              </QueryClientProvider>
            </Grid>
            {lgUp && isProImpersonated && (
              <Grid item className={flexNone}>
                <ProImpersonationLogoutButton />
              </Grid>
            )}
            {smUp && (
              <Grid item className={flexContainer}>
                <ShortcutIcons
                  lightMode={lightMode}
                  onMyAppsClick={onMyAppsClick}
                  showCompanySetup={showCompanySetup}
                  quickbooksBadgeData={quickbooksBadgeData}
                />
              </Grid>
            )}
            <Grid item xs="auto">
              <AvatarMenu
                activeTab={activeTab}
                showCompanySetup={showCompanySetup}
                toggleActivityFeedDrawer={toggleActivityFeedDrawer}
                toggleTasksDrawer={toggleTasksDrawer}
                onMyAppsClick={onMyAppsClick}
                packageLogo={packageLogo}
                quickbooksBadgeData={quickbooksBadgeData}
              />
            </Grid>
          </Grid>
          <AppsPopoverContainer
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onPopoverClose={() => {
              setAnchorEl(null);
            }}
          />
        </Toolbar>
      </AppBar>
      {
        // Do not remove this. This is an empty div that allows the Appbar to be sticky without blocking
        // other parts of the page. i.e) The page content will appear below the main navbar.
      }
      <div style={{ height: navbarHeight }} />
    </>
  );
}

AppToolbar.propTypes = {
  toolbarProps: PropTypes.shape({
    canUsePipeline: PropTypes.bool,
    canUsePriceBook: PropTypes.bool,
    showReporting: PropTypes.bool,
    activeTab: PropTypes.string,
    noticeBar: PropTypes.shape({
      display_message: PropTypes.string,
    }),
    hideNoticeBar: PropTypes.bool,
    showCompanySetup: PropTypes.bool,
    quickbooksBadgeData: PropTypes.shape({
      qboErrorCount: PropTypes.number,
      qbdErrorCount: PropTypes.number,
      qboEnabled: PropTypes.bool,
      qbdEnabled: PropTypes.bool,
    }),
    getStartedDisplaySettings: PropTypes.shape({
      jobs_ftu: PropTypes.bool,
    }),
    isProImpersonated: PropTypes.bool,
    thumbLogoUrl: PropTypes.string,
    lightMode: PropTypes.bool,
    industryPackageName: PropTypes.string,
    pricebookBadgeData: PropTypes.shape({}),
    openDisputesCount: PropTypes.number,
  }),
  toggleActivityFeedDrawer: PropTypes.func,
  toggleTasksDrawer: PropTypes.func,
  legacyToolbar: PropTypes.bool,
};

export default AppToolbar;
